/**
 * PanelTreeMixin — PanelTreeService 퍼사드 wrapper
 */
import { createPanelTreeService } from '../services/panelTreeService.js';
import {
  panelModuleTreeResizePlugin,
  readPanelModuleTreeWidth,
  PANEL_MODULE_TREE_DEFAULT_WIDTH
} from '@/ui/plugins';
import { resolveNextStringDisplayIdInArray } from '../utils/panelEntityNumbering.js';
import {
  normalizeSolarStringApiResult,
  normalizeSolarArrayApiResult,
  buildStringFlatRecordsFromApi,
  buildArrayFlatRecordFromApi,
  buildArrayTreeNodeFromFlat,
  appendStringWithModulesToFlattened,
  buildStringTreeNodeFromFlat,
  ensureArrayInfoInFlattened,
  appendArrayToFlattened,
  removeArrayFromFlattened,
  removeStringFromFlattened,
  removeModuleFromFlattened,
  lookupStringInfo,
  filterSolarStringsAfterStringDelete,
  removeModuleFromSolarStrings
} from '../utils/panelSiteDesignPatch.js';
import { findArrayTreeItemBySeq, findTreeItemById } from '../utils/mapHierarchySelectionContext.js';
import { summarizeTreeBulkSelection } from '../utils/panelTreeBulkSelection.js';
import {
  logStringCopySave,
  warnStringCopySave,
  errorStringCopySave,
  snapshotMapLoadingFlags,
  wouldShowMapLoadingOverlay,
  summarizeRawApiResult
} from '../utils/stringCopySaveDebug.js';
import {
  panelPerfMark,
  panelPerfMeasure,
  panelPerfAsync,
  panelPerfSync,
  panelPerfCountSummary
} from '../utils/panelPlanningPerfLog.js';
import {
  clearPanelFlattenedRegistry,
  getPanelFlattenedRegistry,
  setPanelFlattenedRegistry
} from '../utils/panelFlattenedRegistry.js';
import {
  clearPanelTreeLocalRegistry,
  getPanelTreeLocalRegistry,
  hydrateStringTreeModulesInRegistry,
  removePanelTreeNodeById,
  setPanelTreeLocalRegistry,
  subscribePanelTreeLocalRegistry,
  decrementStringModuleChildCount
} from '../utils/panelTreeLocalRegistry.js';
import { removeModuleFromStringLazyIndex } from '../utils/panelTreeModuleChildrenIndex.js';

export const treeSetup = {
  data() {
    return {
      moduleTreePanelWidth: PANEL_MODULE_TREE_DEFAULT_WIDTH,
      /** getSiteDesign 등 트리 데이터 API 대기 중 */
      moduleTreeLoading: false,
      /** C1 — deferred SET_TREE_DATA 대기 중 skeleton 유지 */
      moduleTreeCommitPending: false,
      /** C4 — local tree registry 구독 시 UI 갱신 */
      panelTreeRevision: 0,
      /** String 생성·복사 등 API in-flight 중첩 깊이 (맵 오버레이) — PanelPlanningPage.data 와 동기 */
      siteDesignSaveDepth: 0,
      entityManager: null,
      flattenedSiteDesign: null,
      /** syncFlattenedToStore — 동일 flattened 참조 재 commit 방지 */
      _lastSyncedFlattenedSiteDesignRef: null,
      solarStrings: []
    };
  },

  computed: {
    _treeSvc() { return createPanelTreeService(); },

    /** 모듈 트리 — API 대기 또는 deferred tree commit 중 skeleton */
    showModuleTreeSkeleton() {
      return !!this.moduleTreeLoading || !!this.moduleTreeCommitPending;
    },

    moduleTreeRowHeight() {
      return 44;
    },

    /** C4 — Vuex 밖 local tree registry */
    treeData() {
      void this.panelTreeRevision;
      return getPanelTreeLocalRegistry();
    },

    treeBulkSelectionSummary() {
      void this.selectionUiVersion;
      void this.treeBulkSelectedIds;
      return summarizeTreeBulkSelection(this.getTreeBulkSelectedItems?.() ?? []);
    },

    showTreeBulkMoveBar() {
      return this.treeBulkSelectionSummary.count >= 2;
    },

    treeBulkMoveDisabledHint() {
      const reason = this.treeBulkSelectionSummary.disableReason;
      if (reason === 'mixed') return this.$t('panelPlanning.treeBulkMoveDisabledMixed');
      if (reason === 'array') return this.$t('panelPlanning.treeBulkMoveDisabledArray');
      return '';
    }
  },

  watch: {
    treeDrawer(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.initModuleTreeResize();
          const tree = this.$refs.moduleVirtualTree;
          if (tree) tree.scheduleVirtualScrollRefresh?.();
        });
      }
    }
  },

  methods: {
    async runWithModuleTreeLoading(task) {
      if (typeof task !== 'function') {
        return undefined;
      }
      panelPerfMark('moduleTreeLoading:on');
      this.moduleTreeLoading = true;
      try {
        return await panelPerfAsync('moduleTreeLoading:task', () => task());
      } finally {
        this.moduleTreeLoading = false;
        panelPerfMark('moduleTreeLoading:off');
      }
    },

    /**
     * String/Module 저장 API 구간 — 맵 스피너·클릭 차단 (트리 skeleton 없음)
     * 중첩 호출(연속 붙여넣기 등)은 같은 세션으로 유지, 동시 사용자 액션은 차단
     */
    async runWithSiteDesignSaving(task) {
      if (typeof task !== 'function') {
        return undefined;
      }

      const depthBefore = Number(this.siteDesignSaveDepth);
      const depth = Number.isFinite(depthBefore) && depthBefore >= 0 ? depthBefore : 0;
      if (depth !== this.siteDesignSaveDepth) {
        warnStringCopySave('runWithSiteDesignSaving → depth reset (was invalid)', {
          was: this.siteDesignSaveDepth,
          now: depth
        });
        this.siteDesignSaveDepth = depth;
      }

      const nested = depth > 0;
      logStringCopySave('runWithSiteDesignSaving → enter', {
        nested,
        depth,
        flags: snapshotMapLoadingFlags(this),
        overlay: wouldShowMapLoadingOverlay(snapshotMapLoadingFlags(this))
      });
      if (!nested && this.isSavingSiteDesign) {
        this.handleShowMessage?.({
          type: 'warning',
          text: this.$t('panelPlanning.siteDesignSaveInProgress')
        });
        return undefined;
      }

      const turnedOverlayOn = !nested;
      if (turnedOverlayOn) {
        this.isSavingSiteDesign = true;
        logStringCopySave('runWithSiteDesignSaving → overlay ON', {
          flags: snapshotMapLoadingFlags(this)
        });
      }
      this.siteDesignSaveDepth = depth + 1;
      try {
        return await task();
      } finally {
        const currentDepth = Number(this.siteDesignSaveDepth);
        const nextDepth =
          Number.isFinite(currentDepth) && currentDepth > 0 ? currentDepth - 1 : 0;
        this.siteDesignSaveDepth = nextDepth;

        if (nextDepth <= 0) {
          this.siteDesignSaveDepth = 0;
          if (turnedOverlayOn) {
            this.isSavingSiteDesign = false;
          }
          logStringCopySave('runWithSiteDesignSaving → overlay OFF', {
            flags: snapshotMapLoadingFlags(this),
            overlay: wouldShowMapLoadingOverlay(snapshotMapLoadingFlags(this))
          });
          const flagsAfter = snapshotMapLoadingFlags(this);
          if (wouldShowMapLoadingOverlay(flagsAfter)) {
            warnStringCopySave('overlay still ON after save session ended — 다른 플래그 확인', flagsAfter);
          }
        } else {
          logStringCopySave('runWithSiteDesignSaving → nested exit (overlay 유지)', {
            depth: nextDepth,
            flags: snapshotMapLoadingFlags(this)
          });
        }
      }
    },

    /** flattened → registry (Vuex 반응성 밖). display id getter 단일 소스 */
    syncFlattenedToStore(options = {}) {
      const force = options?.force === true;
      const flat = this.flattenedSiteDesign;

      if (!flat) {
        if (this._lastSyncedFlattenedSiteDesignRef != null) {
          panelPerfSync('syncFlattenedToStore:clear', () => {
            clearPanelFlattenedRegistry();
          });
          this._lastSyncedFlattenedSiteDesignRef = null;
        }
        panelPerfCountSummary('syncFlattenedToStore:skip', { reason: 'no flattened' });
        return;
      }

      if (!force && flat === this._lastSyncedFlattenedSiteDesignRef) {
        panelPerfCountSummary('syncFlattenedToStore:skip', { reason: 'sameRef' });
        return;
      }

      if (!force && flat === getPanelFlattenedRegistry()) {
        this._lastSyncedFlattenedSiteDesignRef = flat;
        panelPerfCountSummary('syncFlattenedToStore:skip', { reason: 'registrySameRef' });
        return;
      }

      panelPerfSync('syncFlattenedToStore', () => {
        panelPerfCountSummary('syncFlattenedToStore:input', {
          rows: flat.flattened?.length ?? 0,
          modules: flat.byModule?.size ?? 0,
          force
        });
        setPanelFlattenedRegistry(flat);
        this._lastSyncedFlattenedSiteDesignRef = flat;
      });
    },

    /**
     * Array API 성공 후 flattened·트리 index만 증분 반영 (맵 draw·전체 refetch 없음)
     */
    applyArrayCreatedPatch(patch) {
      const { apiResult, requestPayload, sectionId } = patch || {};
      const apiDto = normalizeSolarArrayApiResult(apiResult);
      const resolvedSeq = apiDto?.id;

      if (!apiResult || resolvedSeq == null || !this.flattenedSiteDesign) {
        console.error('applyArrayCreatedPatch: missing array seq or flattened data', {
          resolvedSeq,
          hasFlattened: Boolean(this.flattenedSiteDesign)
        });
        return false;
      }

      try {
        const arrayInfo = buildArrayFlatRecordFromApi(apiDto, {
          flattened: this.flattenedSiteDesign,
          sectionId: sectionId ?? this.siteSectionId,
          requestPayload
        });

        this.flattenedSiteDesign = appendArrayToFlattened(this.flattenedSiteDesign, arrayInfo);

        if (!findArrayTreeItemBySeq(this.treeData, resolvedSeq)) {
          this.$store.commit('panel/moduleTree/ADD_ARRAY_TO_TREE', buildArrayTreeNodeFromFlat(arrayInfo));
        }

        this.syncFlattenedToStore({ force: true });
        this.calculateMaxIdsFromSolarStrings?.();

        const arrayNode =
          findArrayTreeItemBySeq(this.treeData, resolvedSeq) ?? buildArrayTreeNodeFromFlat(arrayInfo);
        this.syncStringDrawTargetFromTreeItem?.(arrayNode);
        this.scrollToArrayFromPatch(resolvedSeq);

        return true;
      } catch (error) {
        console.error('applyArrayCreatedPatch failed:', error);
        return false;
      }
    },

    /**
     * Array API 삭제 성공 후 flattened·트리 index만 증분 반영 (맵·전체 refetch 없음)
     */
    applyArrayDeletedPatch(patch) {
      const { arraySeq, arrayNodeId } = patch || {};
      if (arraySeq == null || !this.flattenedSiteDesign) {
        console.error('applyArrayDeletedPatch: missing array seq or flattened data', {
          arraySeq,
          hasFlattened: Boolean(this.flattenedSiteDesign)
        });
        return false;
      }

      try {
        const arrayKey = String(arraySeq);
        this.flattenedSiteDesign = removeArrayFromFlattened(this.flattenedSiteDesign, arraySeq);

        this.solarStrings = (this.solarStrings || []).filter(
          s => String(s.solarArraySeq ?? s.solarArrayId ?? '') !== arrayKey
        );

        if (arrayNodeId) {
          this.$store.commit('panel/moduleTree/REMOVE_ARRAY_TREE_NODE', arrayNodeId);
        }

        this.syncFlattenedToStore({ force: true });
        this.calculateMaxIdsFromSolarStrings?.();
        return true;
      } catch (error) {
        console.error('applyArrayDeletedPatch failed:', error);
        return false;
      }
    },

    /**
     * String API 삭제 성공 후 flattened·트리 index만 증분 반영 (맵·전체 refetch 없음)
     */
    applyStringDeletedPatch(patch) {
      const { stringSeq, stringNodeId } = patch || {};
      if (stringSeq == null || !this.flattenedSiteDesign) {
        console.error('applyStringDeletedPatch: missing string seq or flattened data', {
          stringSeq,
          hasFlattened: Boolean(this.flattenedSiteDesign)
        });
        return false;
      }

      try {
        const stringInfo = lookupStringInfo(this.flattenedSiteDesign, stringSeq);
        this.flattenedSiteDesign = removeStringFromFlattened(this.flattenedSiteDesign, stringSeq);
        this.solarStrings = filterSolarStringsAfterStringDelete(
          this.solarStrings,
          stringInfo,
          stringSeq
        );

        if (stringNodeId) {
          this.$store.commit('panel/moduleTree/REMOVE_STRING_TREE_NODE', stringNodeId);
        }

        this.syncFlattenedToStore({ force: true });
        this.calculateMaxIdsFromSolarStrings?.();
        return true;
      } catch (error) {
        console.error('applyStringDeletedPatch failed:', error);
        return false;
      }
    },

    /**
     * Module API 삭제 성공 후 flattened·트리 index만 증분 반영 (맵·전체 refetch 없음)
     */
    applyModuleDeletedPatch(patch) {
      const { moduleSeq, moduleNodeId, parentStringNodeId } = patch || {};
      if (moduleSeq == null || moduleSeq === '' || !this.flattenedSiteDesign) {
        console.error('applyModuleDeletedPatch: missing module seq or flattened data', {
          moduleSeq,
          hasFlattened: Boolean(this.flattenedSiteDesign)
        });
        return false;
      }

      try {
        const moduleInfo =
          this.flattenedSiteDesign.byModule?.get(moduleSeq) ??
          this.flattenedSiteDesign.byModule?.get(Number(moduleSeq)) ??
          this.flattenedSiteDesign.byModule?.get(String(moduleSeq));

        this.flattenedSiteDesign = removeModuleFromFlattened(this.flattenedSiteDesign, moduleSeq);
        removeModuleFromSolarStrings(this.solarStrings, moduleInfo, moduleSeq);

        if (moduleNodeId) {
          this.$store.commit('panel/moduleTree/REMOVE_MODULE_TREE_NODE', moduleNodeId);
        }
        if (parentStringNodeId && moduleNodeId) {
          removeModuleFromStringLazyIndex(parentStringNodeId, moduleNodeId);
          decrementStringModuleChildCount(parentStringNodeId);
        }

        this.syncFlattenedToStore({ force: true });
        this.calculateMaxIdsFromSolarStrings?.();
        return true;
      } catch (error) {
        console.error('applyModuleDeletedPatch failed:', error);
        return false;
      }
    },

    scrollToArrayFromPatch(arraySeq) {
      if (arraySeq == null) {
        return;
      }
      const arrayNodeId = `array_${arraySeq}`;
      this.$nextTick(() => {
        this.$refs.moduleVirtualTree?.scrollToNodeId?.(arrayNodeId);
      });
    },

    scrollToStringFromPatch(patch) {
      const { apiResult, requestPayload, arraySeq } = patch || {};
      const apiDto = normalizeSolarStringApiResult(apiResult, requestPayload);
      const stringNodeId = apiDto.id != null ? `string_${apiDto.id}` : null;
      if (!stringNodeId) {
        return;
      }
      this.$nextTick(() => {
        if (arraySeq != null) {
          this.$refs.moduleVirtualTree?.ensureNodeExpanded?.(`array_${arraySeq}`);
        }
        this.$refs.moduleVirtualTree?.scrollToNodeId?.(stringNodeId);
      });
    },

    ensureFlattenedSiteDesignFromRegistry() {
      if (this.flattenedSiteDesign) {
        return true;
      }
      const registry = getPanelFlattenedRegistry();
      if (!registry) {
        return false;
      }
      this.flattenedSiteDesign = registry;
      warnStringCopySave('ensureFlattenedSiteDesignFromRegistry → restored from registry');
      return true;
    },

    syncPanelPlanningLocalStateFromFlattened(options = {}) {
      const {
        syncFlattened = true,
        syncSolarStrings = true,
        syncEntity = true,
        rebuildMapIndex = true,
        refreshStyles = true
      } = options;

      if (syncFlattened) {
        this.syncFlattenedToStore({ force: true });
      }
      if (syncSolarStrings && this.flattenedSiteDesign) {
        this.solarStrings = this._treeSvc.extractSolarStrings(this.flattenedSiteDesign);
      }
      if (syncEntity) {
        this.updateEntityManager?.();
      }
      if (rebuildMapIndex) {
        this._cachedMapFeatureIndex = null;
        this.rebuildPanelMapFeatureIndex?.();
      }
      if (refreshStyles) {
        this.syncModuleLabelVisibilityAfterFeaturesChange?.();
        this.requestVectorStyleRefresh?.();
      }
      this.calculateMaxIdsFromSolarStrings?.();
    },

    reportStringCreatedPatchFailure(patch, reason) {
      errorStringCopySave('applyStringCreatedPatch → failed (no full refetch)', {
        reason,
        patchSummary: {
          arraySeq: patch?.arraySeq,
          api: summarizeRawApiResult(patch?.apiResult)
        }
      });
      this.handleShowMessage?.({
        type: 'error',
        text: this.$t('panelPlanning.stringCreatedPatchFailed')
      });
    },

    /**
     * String+Module API 성공 후 flattened·트리·맵 증분 반영 (전체 refetch/rebuild 없음)
     */
    async applyStringCreatedPatch(patch) {
      const applied = await this._applyStringCreatedPatchIncremental(patch);
      if (applied) {
        return true;
      }

      this.ensureFlattenedSiteDesignFromRegistry();
      const retried = await this._applyStringCreatedPatchIncremental(patch);
      if (retried) {
        this.scrollToStringFromPatch(patch);
        return true;
      }

      this.reportStringCreatedPatchFailure(patch, 'incremental retry exhausted');
      return false;
    },

    async _applyStringCreatedPatchIncremental(patch) {
      const { apiResult, requestPayload, arraySeq } = patch || {};
      logStringCopySave('applyStringCreatedPatch → enter', {
        arraySeq,
        hasFlattened: Boolean(this.flattenedSiteDesign),
        api: summarizeRawApiResult(apiResult),
        requestStringId: requestPayload?.stringId ?? null,
        flags: snapshotMapLoadingFlags(this)
      });

      if (!apiResult || arraySeq == null || !this.flattenedSiteDesign) {
        warnStringCopySave('applyStringCreatedPatch → skip (전제 조건)', {
          hasApiResult: Boolean(apiResult),
          arraySeq,
          hasFlattened: Boolean(this.flattenedSiteDesign)
        });
        return false;
      }

      try {
        const apiDto = normalizeSolarStringApiResult(apiResult, requestPayload);
        logStringCopySave('applyStringCreatedPatch → normalized dto', {
          id: apiDto.id,
          stringId: apiDto.stringId,
          moduleCount: apiDto.solarModules?.length ?? 0
        });

        if (apiDto.id == null) {
          errorStringCopySave('applyStringCreatedPatch → string seq 없음', {
            api: summarizeRawApiResult(apiResult),
            normalized: apiDto
          });
          return false;
        }

        const ensured = ensureArrayInfoInFlattened(this.flattenedSiteDesign, arraySeq, {
          treeData: this.treeData,
          defaultSectionId: this.siteSectionId
        });
        this.flattenedSiteDesign = ensured.flattened;

        const { stringInfo, moduleInfos, arrayInfo, sectionInfo } = buildStringFlatRecordsFromApi(
          apiDto,
          { arraySeq, flattened: this.flattenedSiteDesign, requestPayload }
        );

        this.flattenedSiteDesign = appendStringWithModulesToFlattened(this.flattenedSiteDesign, {
          stringInfo,
          moduleInfos
        });

        const stringNode = buildStringTreeNodeFromFlat(stringInfo, moduleInfos);
        this.$store.commit('panel/moduleTree/ADD_STRING_TO_TREE', {
          arrayNodeId: `array_${arraySeq}`,
          stringNode
        });

        if (!this.vectorSource) {
          warnStringCopySave('applyStringCreatedPatch → vectorSource 없음 — 맵 draw 생략');
        } else {
          this.drawStringPolygonWithFlattenedData?.(stringInfo, arrayInfo, sectionInfo);
          this.drawModulePolygonsWithFlattenedData?.(stringInfo, arrayInfo, sectionInfo);
        }

        if (typeof this.convertFlattenedToStringData === 'function') {
          const solarStringData = this.convertFlattenedToStringData(
            stringInfo,
            arrayInfo,
            sectionInfo
          );
          this.solarStrings = [...(this.solarStrings || []), solarStringData];
        }

        this.syncPanelPlanningLocalStateFromFlattened({
          syncSolarStrings: false
        });

        this.scrollToStringFromPatch({ apiResult, requestPayload, arraySeq });
        logStringCopySave('applyStringCreatedPatch → incremental OK', {
          stringNodeId: `string_${apiDto.id}`,
          arraySeq,
          flags: snapshotMapLoadingFlags(this)
        });
        return true;
      } catch (error) {
        errorStringCopySave('applyStringCreatedPatch → catch', {
          message: error?.message,
          stack: error?.stack
        });
        return false;
      }
    },

    /** Array seq — 다음 String 표시 번호 (flattened + solarStrings) */
    resolveNextStringDisplayIdForArray(arraySeq) {
      return resolveNextStringDisplayIdInArray({
        flattened: this.flattenedSiteDesign,
        solarStrings: this.solarStrings,
        arraySeq
      });
    },

    async initModuleTreeResize() {
      const el = this.$refs.moduleTreePanel;
      if (!el) return;
      this.moduleTreePanelWidth = await readPanelModuleTreeWidth();
      this.teardownModuleTreeResize();
      this._moduleTreeResizeCleanup = panelModuleTreeResizePlugin.attach(el, {
        getWidth: () => this.moduleTreePanelWidth,
        setWidth: w => { this.moduleTreePanelWidth = w; },
        onResize: () => { this.map?.updateSize(); }
      });
    },

    teardownModuleTreeResize() {
      this._moduleTreeResizeCleanup?.();
      this._moduleTreeResizeCleanup = null;
    },

    calculateMaxIds(payload) {
      return this._treeSvc.calculateMaxIds(
        payload?.solarStrings || this.solarStrings
      );
    },

    async calculateMaxIdsHandler() {
      // flattened → Vuex는 load·patch 시 syncFlattenedToStore에서 1회 반영
    },

    updateRelatedData() {
      this.solarStrings = this._treeSvc.extractSolarStrings(this.flattenedSiteDesign);
    },

    updateEntityManager() {
      if (this.entityManager) {
        this.entityManager.initializeFromSolarStrings?.(this.solarStrings || []);
      }
    },

    generateTreeDataFromSiteHandler(payload) {
      const site = payload?.selectedSite ?? this.selectedSite;
      this.$store.commit('panel/moduleTree/SET_SELECTED_SITE', site);
      this.$store.dispatch('panel/moduleTree/generateTreeDataFromSite', {
        site,
        solarStrings: (payload?.solarStrings ?? this.solarStrings) || []
      });
    },

    initializeEntityManager() {
      try {
        this.initializeEntityManagerFromFlattenedData();
      } catch (e) {
        console.warn('EntityManager 초기화 실패:', e);
      }
    },

    initializeEntityManagerFromFlattenedData() {
      if (!this.entityManager || !this.flattenedSiteDesign) return;
      this.entityManager.initializeFromEntityData?.(this.flattenedSiteDesign)
        || this.entityManager.initializeFromSolarStrings?.(this.solarStrings);
    },

    setupEntityManagerListeners() {
      if (!this.entityManager) return;
      this.entityManager.on?.('entitySelected', data => {
        if (data?.entity) this.applyTreeItemMapSelection?.(data.entity);
      });
    },

    isTreeNodeSelected(item) {
      void this.selectionUiVersion;
      const id = item?.id;
      if (id == null) return false;
      const selected = this.treeBulkSelectedIds || [];
      if (selected.length) {
        return selected.some(selectedId => String(selectedId) === String(id));
      }
      return this._treeSvc.isTreeNodeSelected(id, this.lastHighlightedItemId);
    },

    getTreeBulkSelectedItems() {
      const rows = this.$refs.moduleVirtualTree?.getVisibleNodeRows?.() ?? [];
      const byId = new Map(rows.map(row => [row.item?.id, row.item]));
      return (this.treeBulkSelectedIds || [])
        .map(id => byId.get(id) || findTreeItemById(this.treeData, id))
        .filter(Boolean);
    },

    handleTreeNodeBlurred(data) {
      void data;
    },

    handleKeyboardNavigate(data) {
      const { item, key } = data || {};
      const tree = this.$refs.moduleVirtualTree;
      if (!item?.id || !tree?.getVisibleNodeItems) return;
      const items = tree.getVisibleNodeItems();
      const next = this._treeSvc.navigateKeyboardIndex(items, item.id, key);
      if (!next?.id) return;
      this.focusedNodeId = next.id;
      this.$nextTick(() => tree.scrollToNodeId?.(next.id));
    }
  },

  mounted() {
    this._panelTreeRegistryUnsub = subscribePanelTreeLocalRegistry(() => {
      this.panelTreeRevision++;
    });
  },

  beforeDestroy() {
    this._panelTreeRegistryUnsub?.();
    this._panelTreeRegistryUnsub = null;
  }
};
